import React from "react";
import { Box } from "@mui/material";
import {
  CERTIFICATE_SAMPLE_TEXT,
  certificateFontFamily,
  certificateOrientation,
  certificatePageSize,
  certificatePaperColour,
  type CertificateAwardDetails,
  type CertificateRecipient,
  type CertificateTemplate,
} from "../../domain/certificates/certificateTemplates";
import type { SignatureInput } from "../../domain/signatures/signatures";
import { CERTIFICATE_DESIGNS } from "./designs/certificateDesigns";
import { certificateInk, type CertificateText } from "./designs/certificateParts";
import { formatCertificateDate } from "../../domain/certificates/certificateDate";
import { useFitScale } from "../../hooks/useFitScale";

/** A4 landscape at 96 dpi: the frame certificate cards share, whatever their orientation. */
export const { width: CERTIFICATE_WIDTH_PX, height: CERTIFICATE_HEIGHT_PX } =
  certificatePageSize("landscape");

type CertificateCanvasProps = {
  template: CertificateTemplate;
  /**
   * What the Award prints, including who signs it. Certificate templates leave
   * this out and show a placeholder signature; an Award with no signature yet
   * gets a blank line.
   */
  award?: CertificateAwardDetails;
  recipient?: CertificateRecipient;
  /**
   * Fill a box the page's own shape, for exported files. On screen every
   * certificate sits centred in the same landscape frame, so portrait and
   * landscape ones line up and take the same height.
   */
  pageShaped?: boolean;
};

const PLACEHOLDER_SIGNATURE: SignatureInput = {
  name: CERTIFICATE_SAMPLE_TEXT.signatureName,
  title: CERTIFICATE_SAMPLE_TEXT.signatureTitle,
};
const BLANK_SIGNATURE: SignatureInput = { name: "", title: "Signature" };

function resolveSignatures(award: CertificateAwardDetails | undefined): SignatureInput[] {
  if (!award) return [PLACEHOLDER_SIGNATURE];
  const signatures = (award.signatures ?? [])
    .map((signature) => ({ name: signature.name.trim(), title: signature.title.trim() }))
    .filter((signature) => signature.name || signature.title)
    .slice(0, 2);
  return signatures.length > 0 ? signatures : [BLANK_SIGNATURE];
}

function resolveText(
  award: CertificateAwardDetails | undefined,
  recipient: CertificateRecipient | undefined
): CertificateText {
  const name = [recipient?.firstName, recipient?.surname]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
  return {
    certificateTitle:
      award?.certificateTitle?.trim() || CERTIFICATE_SAMPLE_TEXT.certificateTitle,
    recipientName: name || CERTIFICATE_SAMPLE_TEXT.recipientName,
    achievementName:
      award?.achievementName?.trim() || CERTIFICATE_SAMPLE_TEXT.achievementName,
    // Optional on an Award, so a blank one stays blank; sample text is only for template previews.
    description: award
      ? (award.description?.trim() ?? "")
      : CERTIFICATE_SAMPLE_TEXT.description,
    completionDate:
      formatCertificateDate(award?.completionDate) ||
      CERTIFICATE_SAMPLE_TEXT.completionDate,
    certificateId:
      recipient?.certificateId?.trim() || CERTIFICATE_SAMPLE_TEXT.certificateId,
    signatures: resolveSignatures(award),
  };
}

/**
 * Draws one certificate at a fixed A4 size (landscape or portrait), scaled to fit its
 * container. The editor, the Award preview and the exported files all use
 * this, so they can't drift apart. Parts not filled in yet show sample text.
 */
const CertificateCanvas: React.FC<CertificateCanvasProps> = ({
  template,
  award,
  recipient,
  pageShaped = false,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const orientation = certificateOrientation(template);
  const page = certificatePageSize(orientation);
  const frame = pageShaped ? page : certificatePageSize("landscape");
  const scale = useFitScale(containerRef, page);
  const text = resolveText(award, recipient);
  const design = CERTIFICATE_DESIGNS[template.style];

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: `${frame.width} / ${frame.height}`,
        overflow: "hidden",
      }}
    >
      <Box
        role="figure"
        aria-label="Certificate"
        sx={{
          position: "absolute",
          top: 0,
          // Centred across the frame; a no-op when the frame is the page's own shape.
          left: "50%",
          width: page.width,
          height: page.height,
          transform: `translateX(-50%) scale(${scale})`,
          transformOrigin: "top center",
          bgcolor: certificatePaperColour(template),
          color: certificateInk(template).ink,
          overflow: "hidden",
          fontFamily: certificateFontFamily(design.bodyFont),
        }}
      >
        <design.Layout template={template} text={text} orientation={orientation} />
      </Box>
    </Box>
  );
};

export default CertificateCanvas;
